import { useIsFocused, useNavigation } from '@react-navigation/native'
import { StackScreenProps } from '@react-navigation/stack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dimensions, StyleSheet, View } from 'react-native'
import 'react-native-get-random-values'
import { IconButton, Text } from 'react-native-paper'
import Row from '../../../@ui/containers/grid/row'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import {
  Article,
  ArticleQuery,
  Contribution,
  ContributionStatus,
  ContributionType,
  TargetType,
} from '../../../generated/graphql'
import {
  ReplayNavigation,
  ReplayNavigatorParams,
} from '../../../navigation/types'
import { createStyle } from '../../../contexts/global-styles.provider'
import { ReplayLevelIndicator } from '../clusters/shared/LevelIndicator'
import {
  DisplayPerspective,
  DisplayPerspectiveRef,
} from '../perspectives/perspective/display-perspective'
import { useReplayApi } from '../../../services/snapshots/replay-api.hook'
import { useTranslation } from 'react-i18next'
import { CorankTag } from '../../../@ui/buttons/corank.button'

export type ArticleDetails = ArticleQuery['article']

const NoPerspectiveYet = () => {
  const { t } = useTranslation('replay')

  return (
    <Text
      variant="titleLarge"
      style={{
        textAlign: 'center',
        color: 'gray',
        marginTop: 64,
        fontWeight: 'bold',
      }}
    >
      {t('perspective-not-found')}
    </Text>
  )
}
function PerspectiveScreenImplementation({ articleId }: { articleId: string }) {
  /**
   * Basics hooks
   */
  const isFocused = useIsFocused()
  const styles = useStyles()
  const navigation = useNavigation<ReplayNavigation>()
  const displayPerspectiveRef = useRef<DisplayPerspectiveRef>(null)
  const { perspectiveService, tick } = useReplayApi()
  const [perspective, setPerspective] = useState<Article | null>(null)

  const doRefresh = useCallback(async () => {
    const perspective = await perspectiveService.getPerspective(articleId)

    setPerspective(perspective)
  }, [perspectiveService])

  useEffect(() => {
    doRefresh()
  }, [tick, doRefresh])
  /**
   * State of the screen
   */

  const contributions: Contribution[] = useMemo(() => {
    return perspective?.contributions || []
  }, [perspective])

  useEffect(() => {
    if (isFocused) {
      doRefresh()
    }
  }, [isFocused])

  return (
    <ScreenWrapper withScrollView={false} style={styles.container}>
      {
        <Row
          style={{ paddingHorizontal: 12, paddingTop: 4 }}
          justifyContent="space-between"
          wrap
        >
          <Row alignItems="center" columnGap={4}>
            <IconButton
              mode={'outlined'}
              icon="arrow-left"
              onPress={() =>
                perspective
                  ? navigation.push('ReplayClusterCards', {
                      clusterId: perspective.clusterId,
                    })
                  : navigation.goBack()
              }
            />
          </Row>

          <Row alignItems="center" columnGap={4}>
            {perspective && (
              <ReplayLevelIndicator
                targetId={perspective.id}
                target={TargetType.Article}
              />
            )}
          </Row>
        </Row>
      }
      {perspective && (
        <Row
          style={{
            paddingHorizontal: 24,
            paddingBottom: 10,
          }}
        >
          <Text variant={'headlineSmall'} style={{ marginRight: 12 }}>
            {perspective.title}
          </Text>
          <CorankTag corank={perspective.minCoRank} />
        </Row>
      )}

      {!perspective && <NoPerspectiveYet />}
      {perspective && (
        <DisplayPerspective
          ref={displayPerspectiveRef}
          key={articleId}
          readonly={true}
          perspective={perspective}
          contributions={contributions}
          displayVerticalIndicator={true}
          refetch={doRefresh}
          onFullScreen={(contributionId) =>
            navigation.navigate('ReplayContribution', {
              contributionId,
            })
          }
        />
      )}
    </ScreenWrapper>
  )
}

export default function PerspectiveReplayScreen({
  route,
}: StackScreenProps<ReplayNavigatorParams, 'ReplayPerspective'>) {
  const { articleId } = route.params
  /*
   * React Native reuses the page component,
   * causing a cache effect and displaying previous article content.
   *
   * This is a workaround to force a new component to be created when the articleId changes.
   */
  return (
    <PerspectiveScreenImplementation key={articleId} articleId={articleId} />
  )
}

const useStyles = createStyle(({ theme }) =>
  StyleSheet.create({
    container: {
      flexDirection: 'column',
      display: 'flex',
      backgroundColor: 'white',
      alignItems: 'stretch',
      overflow: 'hidden',
      height: Dimensions.get('window').height - 64,
    },
    editor: {
      flex: 1,
      flexShrink: 1,
      backgroundColor: 'white',
      paddingHorizontal: theme.spacing(2),
    },
    info: {
      paddingVertical: theme.spacing(4),
      paddingHorizontal: theme.spacing(3),
    },
  })
)
